import { useContext, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { AuthContext } from '@/contexts/authContextDef'
import { api } from '@/lib/api'
import { TERMS_URL } from '@/lib/legalLinks'

// EP-160 (section 2.7, D5): an owner whose accepted terms are not the terms in
// force sees this once at the top of the page. A banner, never a gate: closing
// it blocks nothing, and nothing else in the app reads the acceptance.
//
// "Once" is kept per browser: accepting or closing stores the current terms
// version under the owner's id, so the banner does not come back on the next
// page or sign-in until the terms change again. The session user in
// AuthContext is not refreshed after accepting; this marker is also what keeps
// the banner closed for the rest of the session.
const HANDLED_KEY_PREFIX = 'flowrent_terms_banner_'

function readHandledVersion(userId: number): string | null {
  try {
    return localStorage.getItem(`${HANDLED_KEY_PREFIX}${userId}`)
  } catch {
    return null
  }
}

function storeHandledVersion(userId: number, version: string) {
  try {
    localStorage.setItem(`${HANDLED_KEY_PREFIX}${userId}`, version)
  } catch {
    // Storage unavailable (private mode): the banner simply closes for now.
  }
}

export interface TermsBannerProps {
  className?: string
}

export function TermsBanner({ className = '' }: TermsBannerProps) {
  // useContext rather than useAuth: the layout also renders where no
  // AuthProvider is mounted, and the banner then has nobody to show to.
  const user = useContext(AuthContext)?.user
  const currentVersion = user?.terms_current_version

  if (!user || user.role !== 'owner' || !currentVersion) return null
  if (user.terms_version === currentVersion) return null

  return <TermsBannerAlert key={user.id} userId={user.id} version={currentVersion} className={className} />
}

interface TermsBannerAlertProps {
  userId: number
  version: string
  className: string
}

function TermsBannerAlert({ userId, version, className }: TermsBannerAlertProps) {
  const { t } = useTranslation('auth')
  const [handled, setHandled] = useState(() => readHandledVersion(userId) === version)

  const close = () => {
    storeHandledVersion(userId, version)
    setHandled(true)
  }

  const accept = useMutation({
    mutationFn: async () => {
      await api.post('/api/terms_acceptance')
    },
    onSuccess: close,
  })

  if (handled) return null

  return (
    <Alert variant="info" className={className} data-testid="terms-banner">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-medium">{t('terms_banner.message')}</span>
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="link">
          {t('terms_banner.read')}
        </a>
        <div className="flex items-center gap-2 ml-auto">
          <Button size="sm" loading={accept.isPending} onClick={() => accept.mutate()}>
            {t('terms_banner.accept')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            shape="circle"
            aria-label={t('terms_banner.dismiss')}
            onClick={close}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {accept.isError && (
        <p role="alert" className="mt-2 text-error">
          {t('terms_banner.accept_failed')}
        </p>
      )}
    </Alert>
  )
}
